(ns nl.mediquest.be-my-coach-frontend.views.zorgverleners
  (:require
   ["react" :refer [useRef]]
   [clojure.string :as str]
   [nl.mediquest.be-my-coach-frontend.config :as config]
   [nl.mediquest.be-my-coach-frontend.events :as events]
   [nl.mediquest.be-my-coach-frontend.subs :as subs]
   [nl.mediquest.be-my-coach-frontend.util
    :refer [separate-scroll-context]]
   [nl.mediquest.be-my-coach-frontend.views.react-helpers
    :refer [scroll-into-view]]
   [re-frame.core :as re-frame]))

(defn gmaps-url
  [{:keys [latitude longitude] :as _zorgverlener}]
  (str
   "https://www.google.com/maps/dir//"
   (js/encodeURIComponent latitude)
   ","
   (js/encodeURIComponent longitude)
   "/data=!3m1!4b1!4m2!4m1!3e1"))

(defn- minuut-weergave
  [reistijd]
  (case reistijd
    0 "minder dan 1 minuut"
    1 "1 minuut"
    (str reistijd " minuten")))

(defn- adres
  [{:keys [straatnaam huisnummer huisnummer-toevoeging postcode plaatsnaam]
    :as _zorgverlener}]
  (str straatnaam
       " " huisnummer
       " " huisnummer-toevoeging
       ", " postcode
       " " plaatsnaam))

(defn- format-website-name
  [website-url]
  (try
    (.-hostname (js/URL. website-url))
    (catch js/Error _ website-url)))

(defn- telephone-link
  [telephone-number]
  [:a.text-bmc-primary
   {:href (str "tel:" telephone-number)}
   telephone-number])

(defn- website-link
  [website-url]
  (let [hostname (format-website-name website-url)]
    [:a.text-bmc-primary
     {:href website-url :target :_blank}
     hostname]))

(defn- email-link
  [emailaddress]
  [:a.text-bmc-primary
   {:href (str "mailto:" emailaddress)
    :target :_blank}
   emailaddress])

(defn- select-text
  [^js text-element]
  (when (.-getSelection js/window)
      (let [selection (.getSelection js/window)
            range (js/document.createRange)]
        (.removeAllRanges selection)
        (.selectNodeContents range text-element)
        (.addRange selection range))))

(defn- copy-handler
  [^js text-ref]
  (when-let [text-element (.-current text-ref)]
    (.. js/navigator -clipboard
        (writeText (.-innerText text-element))
        (then #(select-text text-element))
        (catch js/console.error))))

(defn- click-to-copy
  [content]
  (let [text-ref (useRef)]
    [:<>
     [:span {:ref text-ref :on-click #(copy-handler text-ref)} content]
     [:i.far.fa-copy.ml-1.hover:cursor-pointer
      {:on-click #(copy-handler text-ref)}]]))

(defn zorgverlener-popup
  [{:keys [naam type-interventie type-zorgverlener] :as zorgverlener}]
  [:<>
   [:h1.font-bold.py-2.text-gray-900
    naam]
   (when type-zorgverlener
     [:div
      (str/join " - " type-zorgverlener)])
   (when type-interventie
     [:div
      (str/join " - " (map (config/locale :nl) type-interventie))])
   [:div.mt-2
    [:a {:href (gmaps-url zorgverlener) :target :_blank}
     "Open in Google Maps"
     [:i.fa.fa-external-link.ml-1]]]])

(defn- zorgverlener-item [label value]
  [:p.text-sm [:span.font-bold (str label " ")] value])

(defn- zorgverlener-card-content
  [{:keys [naam type-zorgverlener type-interventie telefoonnummer website
           emailadres viduet-code reistijd-kortste-minuut]
    :as zorgverlener}]
  [:div.p-2.space-y-2
   {:class ["group/zorgverlener-card"]}
   [:div.flex.flex-row.justify-between.space-x-2
    [:h1.text-md.font-bold.text-gray-900.pt-2
     naam]
    [:a
     {:href (gmaps-url zorgverlener)
      :target :_blank
      :title "Open in Google maps"}
     [:i.fa.fa-location-dot.text-bmc-primary.text-3xl.relative]
     [:i.fa.fa-location-dot.text-bmc-primary.text-3xl.absolute.right-8.opacity-50
      {:class ["group-hover/zorgverlener-card:animate-ping"]}]]]
   (when reistijd-kortste-minuut
     [zorgverlener-item
      "Reistijd (auto):"
      (minuut-weergave reistijd-kortste-minuut)])
   [:div
    [zorgverlener-item
     "Adres:"
     (adres zorgverlener)]
    (when telefoonnummer
      [zorgverlener-item
       "Telefoonnummer:"
       [telephone-link telefoonnummer]])
    (when website
      [zorgverlener-item
       "Website:"
       [website-link website]])
    (when emailadres
      [zorgverlener-item
       "Emailadres:"
       [email-link emailadres]])
    (when viduet-code
      [zorgverlener-item
       "Viduet-code:"
       [:f> click-to-copy viduet-code]])]
   [:div
    (when type-zorgverlener
      [zorgverlener-item
       (str "Type" \u00A0 "zorgverlener:")
       (str/join " - " type-zorgverlener)])
    (when type-interventie
      [zorgverlener-item
       (str "Type" \u00A0 "interventie:")
       (str/join " - " (map (config/locale :nl) type-interventie))])]])

(defn- dim-card?
  [id]
  (when-let [highlighted-id @(re-frame/subscribe [::subs/highlighted-zorgverlener-id])]
    (not= id highlighted-id)))

(defn- zorgverlener-card
  [{:keys [id] :as zorgverlener}]
  [:div.card.bg-white.p-2.overflow-x-auto.rounded-md
   {:class (when (dim-card? id) ["opacity-50"])
    :on-click #(re-frame/dispatch [::events/highlight-zorgverlener id])}
   [zorgverlener-card-content zorgverlener]])

(defn view
  []
  [:div.flex.flex-col.bg-bmc-background.h-full
   (if-let [zorgverleners (not-empty @(re-frame/subscribe [::subs/zorgverleners]))]
     [:<>
      [:div.text-sm.p-4
       "Aantal resultaten: "
       [:span.font-medium (count zorgverleners)]]
      [:div.flex-grow
       [separate-scroll-context
        (let [highlighted-id (re-frame/subscribe [::subs/highlighted-zorgverlener-id])]
          [:div.flex.flex-col.px-4.pb-4.space-y-4
           (doall
            (for [{id :id :as zorgverlener} zorgverleners]
              ^{:key (hash id)}
              [:f> scroll-into-view
               {:pred (= id @highlighted-id)
                :scroll-options #js {:behavior "smooth"}}
               [zorgverlener-card zorgverlener]]))])]]]
     [:div.text-center.w-full.py-4 "Geen zorgverleners om te tonen."])])
